import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import { JSX, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { IconArrowLeft } from "@/components/icons";

import { cn } from "@/utils/cn.ts";
import { isMobile } from "@/utils/constatns";

interface ItemWithId {
  id: string | number;
}

interface CarouselProps<T> {
  swiperParams?: SwiperProps;
  items: T[];
  // eslint-disable-next-line no-unused-vars
  children: ReactNode | ((item: T, index: number) => ReactElement);
  swiperSlideClassName?: string;
  swiperName: string;
  className?: string;
  previousClassName?: string;
  nextClassName?: string;
}

const hasId = (item: unknown): item is ItemWithId => {
  return item !== null && typeof item === "object" && "id" in item;
};

const Carousel = <T,>({
  swiperParams = {},
  items,
  children,
  className,
  swiperName,
  swiperSlideClassName,
  previousClassName,
  nextClassName,
}: CarouselProps<T>): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className={"group/swiper relative"}>
      <Swiper
        freeMode={{
          enabled: true,
          momentumBounce: true,
          momentum: true,
          momentumVelocityRatio: 1,
          momentumBounceRatio: 1,
          momentumRatio: 1,
        }}
        speed={isMobile ? 300 : 600}
        modules={[Navigation, FreeMode]}
        navigation={{
          prevEl: `.js-${swiperName}-prev`,
          nextEl: `.js-${swiperName}-next`,
        }}
        className={cn("overflow-visible", { className })}
        {...swiperParams}
        followFinger={isMobile}
      >
        {items.map((item, index) => (
          <SwiperSlide key={hasId(item) ? item.id : index} className={swiperSlideClassName}>
            {typeof children === "function"
              ? (children as (item: T, index: number) => ReactElement)(item, index)
              : children}
          </SwiperSlide>
        ))}
      </Swiper>
      <button
        type={"button"}
        aria-label={t("navigation.previous")}
        style={{ background: "linear-gradient(270deg, rgba(0, 0, 0, 0.00) 7.17%, var(--surface) 97.86%)" }}
        className={cn(
          `js-${swiperName}-prev responsive-transition group invisible absolute -left-6 top-1/2 z-[100] flex h-full w-20  -translate-y-1/2 transform items-center justify-center opacity-0 hover:bg-orange-1 disabled:!invisible disabled:!opacity-0 group-hover/swiper:visible group-hover/swiper:opacity-100 max-lg:hidden`,
          previousClassName,
        )}
      >
        <IconArrowLeft name={"arrow-left"} className={"size-10 stroke-on-primary"} />
      </button>
      <button
        type={"button"}
        aria-label={t("navigation.next")}
        style={{ background: "linear-gradient(90deg, rgba(0, 0, 0, 0.00) 7.17%, var(--surface) 97.86%)" }}
        className={cn(
          `js-${swiperName}-next responsive-transition group invisible absolute -right-6 top-1/2 z-[100] flex h-full w-20  -translate-y-1/2 transform items-center justify-center opacity-0 hover:bg-orange-1 disabled:!invisible disabled:!opacity-0 group-hover/swiper:visible group-hover/swiper:opacity-100 max-lg:hidden`,
          nextClassName,
        )}
      >
        <IconArrowLeft name={"arrow-right"} className={"size-10 rotate-180 stroke-on-primary"} />
      </button>
    </div>
  );
};

export default Carousel;
